import { userAtom } from "@/Atoms";
import AuthorContent from "@/components/AuthorContent";
import Layout from "@/components/elements/Layout";
import SubNavigation from "@/components/elements/SubNavigation";
import LockedContent from "@/components/LockedContent";
import MentionedVCAndStartup from "@/components/MentionedVCAndStartup";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import ShareButtons from "@/components/parts/ShareButtons";
import ShareButtonsVertical from "@/components/parts/ShareButtonsVertical";
import RelatedContentSidebar from "@/components/RelatedContentSidebar";
import RelatedReport from "@/components/RelatedReport";
import Schema from "@/components/schema/Schema";
import Subscribe from "@/components/Subscribe";
import {
  useGetContentsByContentType,
  useGetCurrentUrl,
  useGetRelatedContents,
} from "@/hooks";
import useAuth from "@/hooks/auth";
import {
  getContentDetailBySlug,
  getPinnedByContentId,
  trackContentAccessCounting,
} from "@/services/api";
import { cleanHTML, CONTENT_TYPES } from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import formatNumber from "@/utils/formatNumber";
import getIcon from "@/utils/getIcon";
import { SealCheck } from "@phosphor-icons/react";
import { fitIn } from "@/utils/imageHandler";
import isPersonName from "@/utils/isPersonName";
import { useAtom } from "jotai";
import Head from "next/head";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import ImageLogo from "../../assets/images/nav-logo-vertical.svg";
import CalendarGradient from "@/assets/images/CalendarGradient.png";
import EmployeeGradient from "@/assets/images/EmployeeGradient.png";
import GlobeGradient from "@/assets/images/GlobeGradient.png";
import HeadquarterGradient from "@/assets/images/HeadquarterGradient.png";
import OperationalGradient from "@/assets/images/OperationalGradient.png";
import PinGradient from "@/assets/images/PinGradient.png";
import FundingStage from "@/components/chart/FundingStage";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function ArticleDetail({ article }) {
  const [reach_limit, setReachLimit] = useState(false);
  const [metered, setMetered] = useState(false);
  const [userState, setUserState] = useAtom(userAtom);
  const [expandedAuthors, setExpandedAuthors] = useState({});
  const router = useRouter();
  const { user } = useAuth();
  const [related_items, isLoading] = useGetRelatedContents(
    CONTENT_TYPES.ARTICLES,
    article.uuid,
  );

  // const [article, setArticle] = useState(props.article ?? {});
  const [otherReports, otherReportLoading] = useGetContentsByContentType(
    CONTENT_TYPES.SPECIAL_REPORTS,
  );

  const [pinneds, setPinneds] = useState({
    startups: [],
    ventureCapitals: [],
  });

  // const loadArticle = async () => {
  //   try {
  //     const response = await getContentDetailBySlug(router.query.slug);
  //     setArticle(response.data.data);
  //   } catch {
  //     // TODO Handle error
  //   }
  // };

  const fetchMeteredContent = async () => {
    if (
      userState &&
      (userState.roles === "admin-team" || userState.roles === "read-access")
    ) {
      setReachLimit(false);
    } else {
      try {
        const response = await trackContentAccessCounting(router.query.slug, {
          ip_address: localStorage.getItem("lctd-ia"),
          user_id: userState?.id,
          user_email: userState?.email,
        });
        setReachLimit(response.data.reach_limit);
      } catch (error) {
        console.log(error);
      }
    }
    setMetered(true);
  };

  useEffect(() => {
    getPinned();
    fetchMeteredContent();
  }, [article.slug, userState]);

  const fullUrl = useGetCurrentUrl();

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      mainEntityOfPage: {
        "@type": "WebPage",
        id: fullUrl,
      },
      headline: article.meta_title,
      image: [article.thumbnail_url],
      datePublished: article.publish_date,
      dateModified: article.publish_date,
      author: {
        "@type": isPersonName(article.author.name) ? "Person" : "Organization",
        name: isPersonName(article.author.name)
          ? article.author.name
          : "Lucidity Insights",
      },
      publisher: {
        "@type": "Organization",
        name: "Lucidity Insights",
        logo: {
          "@type": "ImageObject",
          url: ImageLogo.src,
        },
      },
      description: article.meta_desc,
    },
  ];

  const getPinned = async () => {
    setPinneds({
      startups: [],
      ventureCapitals: [],
    });
    try {
      const res = await getPinnedByContentId(article.id);
      setPinneds(res.data.data);
    } catch (error) {
      setPinneds({
        startups: [],
        ventureCapitals: [],
      });
    }
  };

  const transformYoutubeLinks = (content) => {
    const youtubeRegex =
      /@(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?:&\S+)?/g;

    return content.replace(youtubeRegex, (match, videoId) => {
      const iframe = `
        <div class="relative aspect-video w-full my-6">
          <iframe
            class="absolute top-0 left-0 w-full h-full rounded-lg"
            src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&showinfo=0"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      `;
      return iframe;
    });
  };

  const toggleAuthorDescription = (authorId) => {
    setExpandedAuthors((prev) => ({
      ...prev,
      [authorId]: !prev[authorId],
    }));
  };

  return (
    <div>
      <Head>
        <meta
          property='article:published_time'
          content={new Date(article.publish_date).toISOString()}
        />
        <meta
          property='article:modified_time'
          content={new Date(article.publish_date).toISOString()}
        />
        <meta property='article:section' content='Article Section' />
      </Head>
      <Schema data={structuredData} />
      <Layout
        title={article.title}
        meta_title={article.meta_title}
        description={article.meta_desc}
        meta_image={article.cover_image_url}
      >
        <SubNavigation title={article.title}></SubNavigation>
        <div className='px-4 mx-auto max-w-7xl lg:flex'>
          <div className='flex items-start flex-1 pt-6 pb-10 lg:pt-10 lg:pb-16 lg:pr-8 lg:mr-8 lg:border-r lg:space-x-8 2xl:space-x-12 border-white/5'>
            <div className='sticky hidden pt-2 lg:block top-40'>
              <ShareButtonsVertical
                title={article.title}
                description={article.meta_desc}
                url={fullUrl}
              />
            </div>
            <div className='relative w-full space-y-10 lg:space-y-16 max-w-screen-2xl'>
              <article className='relative mx-auto'>
                <h1 className='text-3xl leading-[1.1] font-bold tracking-tight lg:text-5xl font-display'>
                  {article.title}
                </h1>

                <div className='flex items-center justify-between gap-4 mt-3'>
                  <p className='flex items-center text-sm'>
                    <span className='opacity-40'>
                      {`${formatDate(article.publish_date, "DD MMMM YYYY")}`}
                    </span>
                    <span className='ml-2 opacity-40'>&bull;</span>
                    <ContentWishlistComponent item={article} />
                  </p>
                  <span className='flex-1 h-px bg-white/10'></span>
                  <div className='lg:hidden'>
                    <ShareButtons
                      title={article.title}
                      description={article.meta_desc}
                      url={fullUrl}
                    />
                  </div>
                </div>

                <div className='pt-3 pb-6 text-sm text-white/70'>
                  By{" "}
                  {article.authors.map((author, index) => (
                    <Link
                      key={index}
                      href={`/author/${author.slug}`}
                      className='no-underline'
                    >
                      <span className='underline'>{author.name}</span>
                      {article.authors.length > 2 &&
                        index < article.authors.length - 2 &&
                        ", "}
                      {article.authors.length > 1 &&
                        index === article.authors.length - 2 &&
                        " and "}
                    </Link>
                  ))}{" "}
                </div>

                {article.article_startup && (
                <div className='flex flex-col items-center gap-6 p-4 mb-6 bg-white border border-gray-200 lg:flex-row rounded-2xl'>
                  <div className='flex items-center justify-center flex-1'>
                    <FundingStage
                      fundraised={article.article_startup.total_round_amount}
                      data={article.article_startup.grouped_funding_rounds}
                      width={280}
                      height={280}
                      key={article.article_startup.slug}
                      isLoading={false}
                      lightMode={true}
                      legendPosition='bottom'
                    />
                  </div>
                  <div className='w-full text-gray-900 border-gray-200 border-dashed divide-y divide-gray-200 lg:pl-6 lg:border-l divide-dashed'>
                    {article.article_startup.year_established &&
                    article.article_startup.year_established > 0 ? (
                      <div className='flex items-center justify-between py-3'>
                        <div className='flex items-center space-x-2 text-xs'>
                          <img
                            src={CalendarGradient.src}
                            className='w-5 h-5'
                            alt='Calendar Image'
                          />
                          <span>
                            {article.article_startup.year_established}
                          </span>
                        </div>
                        <span className='flex-shrink-0 ml-auto text-xs text-gray-400'>
                          Established
                        </span>
                      </div>
                    ) : (
                      ""
                    )}
                    {article.article_startup.country && (
                      <div className='flex items-center justify-between py-3'>
                        <div className='flex items-center space-x-2 text-xs'>
                          <img
                            src={HeadquarterGradient.src}
                            className='w-5 h-5'
                            alt='HeadquarterGradient Image'
                          />
                          <span>
                            {article.article_startup.city
                              ? `${article.article_startup.city.name}, `
                              : ""}
                            {article.article_startup.country?.country_name}
                          </span>
                        </div>
                        <span className='flex-shrink-0 ml-auto text-xs text-gray-400'>
                          Headquarter
                        </span>
                      </div>
                    )}
                    {article.article_startup.operational_in_names &&
                      article.article_startup.operational_in_names.length >
                        0 && (
                        <div className='flex items-center justify-between py-3'>
                          <div className='flex items-center space-x-2 text-xs'>
                            <img
                              src={OperationalGradient.src}
                              className='w-5 h-5'
                              alt='OperationalGradient Image'
                            />
                            <div className='flex flex-wrap gap-1'>
                              {article.article_startup.operational_in_names.map(
                                (item) => (
                                  <TooltipProvider key={item.prefix}>
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <Badge
                                          variant='outline'
                                          className='font-medium text-gray-700 bg-gray-100 border-gray-300'
                                        >
                                          {item.prefix}
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{item.name || item.prefix}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ),
                              )}
                            </div>
                          </div>
                          <span className='flex-shrink-0 ml-2 text-xs text-gray-400'>
                            Operational Market
                          </span>
                        </div>
                      )}
                    <div className='flex items-center justify-between py-3'>
                      <div className='flex items-center space-x-2 text-xs'>
                        <img
                          src={EmployeeGradient.src}
                          className='w-5 h-5'
                          alt='EmployeeGradient Image'
                        />
                        <span>
                          {!article.article_startup.employee_range_min &&
                          !article.article_startup.employee_range_max
                            ? "-"
                            : article.article_startup.employee_range_max ===
                                null
                              ? "10,001+"
                              : `${article.article_startup.employee_range_min?.toLocaleString()} - ${article.article_startup.employee_range_max?.toLocaleString()}`}
                        </span>
                      </div>
                      <span className='flex-shrink-0 ml-auto text-xs text-gray-400'>
                        Employees
                      </span>
                    </div>
                    {article.article_startup.current_stage?.name && (
                      <div className='flex items-center justify-between py-3'>
                        <div className='flex items-center space-x-2 text-xs'>
                          <img
                            src={PinGradient.src}
                            className='w-5 h-5'
                            alt='PinGradient Image'
                          />
                          <span>
                            {article.article_startup.current_stage?.name}
                          </span>
                        </div>
                        <span className='flex-shrink-0 ml-auto text-xs text-gray-400'>
                          Latest Known Stage
                        </span>
                      </div>
                    )}
                    {article.article_startup.website && (
                      <div className='flex items-center justify-between py-3'>
                        <div className='flex items-center space-x-2 text-xs'>
                          <img
                            src={GlobeGradient.src}
                            className='w-5 h-5'
                            alt='GlobeGradient Image'
                          />
                          <a
                            href={
                              article.article_startup.website.startsWith(
                                "http",
                              )
                                ? article.article_startup.website
                                : `https://${article.article_startup.website}`
                            }
                            target='_blank'
                            rel='noopener noreferrer'
                            className='transition-colors hover:text-gray-900 hover:underline'
                          >
                            {article.article_startup.website}
                          </a>
                        </div>
                        <span className='flex-shrink-0 ml-auto text-xs text-gray-400'>
                          Website
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {!article.article_startup && (
                <div className='relative w-full h-auto'>
                  <Image
                    src={fitIn(article.cover_image_url, 1200)}
                    alt={article.cover_image_alt}
                    width={640}
                    height={320}
                    layout={"responsive"}
                    objectFit={"cover"}
                    className='rounded-[15px]'
                  />
                </div>
                )}

                <div className='pt-6 mx-auto prose lg:pt-8 content prose-invert prose-img:rounded-lg'>
                  {reach_limit
                    ? cleanHTML(article.description.slice(0, 850))
                    : cleanHTML(transformYoutubeLinks(article.description))}
                </div>
                {reach_limit && (
                  <div className='absolute bottom-0 w-full pt-40 bg-gradient-to-b from-background/0 via-background/90 to-background'>
                    <LockedContent
                      title="You've Reached Your Monthly Limit"
                      description='You are currently on our free plan. Upgrade to enjoy unlimited access.'
                      isLogin={user?.id}
                      blur={false}
                    />
                  </div>
                )}
              </article>
              <MentionedVCAndStartup
                pinneds={pinneds}
                locked={reach_limit}
                isLogin={user?.id}
              />
              {article.article_startup?.funding_rounds?.length > 0 && (
              <div>
                <h3 className='mb-8 text-lg font-semibold lg:text-3xl'>
                  {article.article_startup.name}{"'"}s Investors by Funding Rounds
                </h3>
                <div className='-mx-4 overflow-hidden border-y lg:mx-0 lg:border lg:rounded-xl'>
                  <Table
                    className='table-fixed min-w-[700px]'
                    containerClassName='max-h-[500px] [&::-webkit-scrollbar]:hidden [scrollbar-width:none]'
                  >
                    <TableHeader className='sticky top-0 z-10 bg-muted/50 backdrop-blur-md'>
                      <TableRow className='divide-x border-white/10 divide-white/10'>
                        <TableHead className='text-xs w-[150px] font-semibold text-white'>
                          Funding Round
                        </TableHead>
                        <TableHead className='text-xs w-[130px] font-semibold text-white'>
                          Announced Date
                        </TableHead>
                        <TableHead className='text-xs font-semibold text-white'>
                          Disclosed Investors
                        </TableHead>
                        <TableHead className='text-xs w-[130px] font-semibold text-right text-white'>
                          Round Amount (USD)
                        </TableHead>
                        <TableHead className='text-xs w-[125px] font-semibold text-white'>
                          Data Confidence{" "}
                          <span className='text-red-500'>*</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className='divide-y divide-dashed divide-white/10 text-white/50'>
                      {article.article_startup.funding_rounds.map((round) => (
                        <TableRow
                          key={round.id}
                          className='divide-x border-white/10 divide-white/10'
                        >
                          <TableCell className='py-2 text-base font-medium text-white'>
                            <div className='text-sm'>
                              {round.funding_type.name}
                            </div>
                          </TableCell>
                          <TableCell className='py-2 text-sm font-medium text-white whitespace-nowrap'>
                            {formatDate(round.date_announced, "DD MMM YYYY")}
                          </TableCell>
                          <TableCell className='px-3 text-sm'>
                            <div className='-my-2 divide-y divide-dashed divide-white/10'>
                              {round.investors.length > 0 ||
                              round.investor_labels?.length > 0 ? (
                                <>
                                  {round.investors.map((investor) =>
                                    investor.is_published ? (
                                      <a
                                        key={investor.id}
                                        href={`/investors/${investor.slug}-${investor.id}`}
                                        className='flex py-2 space-x-3 group'
                                      >
                                        {investor.logo_url && (
                                          <div className='flex-shrink-0 w-[22px] h-[22px]'>
                                            <Image
                                              width={22}
                                              height={22}
                                              objectFit='cover'
                                              src={investor.logo_url}
                                              alt=''
                                              className='overflow-hidden rounded-[6px]'
                                            />
                                          </div>
                                        )}
                                        <p className='text-xs text-white group-hover:underline'>
                                          {investor.name}
                                        </p>
                                      </a>
                                    ) : (
                                      <div
                                        key={investor.id}
                                        className='flex-1 min-w-0'
                                      >
                                        <p className='py-2 text-xs text-white'>
                                          {investor.name}
                                        </p>
                                      </div>
                                    ),
                                  )}
                                  {round.investor_labels?.map((label, i) => (
                                    <div
                                      key={`label-${i}`}
                                      className='flex-1 min-w-0'
                                    >
                                      <p className='py-2 text-sm text-white'>
                                        {label.name}
                                        <span className='ml-2 text-xs text-gray-400'>
                                          (label)
                                        </span>
                                      </p>
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <p className='text-sm text-white/40'>
                                  Undisclosed
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className='py-2 text-xl text-right text-white whitespace-nowrap'>
                            {round.amount_undisclosed === 1 ? (
                              <p className='text-sm text-white/40'>
                                Undisclosed
                              </p>
                            ) : (
                              <p>
                                {round.round_amount
                                  ? `${formatNumber(round.round_amount)}`
                                  : "-"}
                              </p>
                            )}
                            {(round.investors.length > 0 ||
                              round.investor_labels?.length > 0) && (
                              <p className='text-xs text-white/40 mt-0.5'>
                                {round.investors.length +
                                  (round.investor_labels?.length || 0)}{" "}
                                Investors
                              </p>
                            )}
                          </TableCell>
                          <TableCell className='py-2 text-sm font-medium'>
                            {!!round.is_verified ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className='inline-flex items-center text-blue-500 cursor-pointer'>
                                      <SealCheck
                                        weight='fill'
                                        className='mr-1 text-lg'
                                      />
                                      <span className='border-b border-dashed border-white/30'>
                                        Verified
                                      </span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className='max-w-xs text-xs font-normal leading-snug'>
                                    A funding round is verified either through
                                    an external news source or when Lucidity
                                    Insights&apos; verification team confirms it
                                    using multiple reliable sources.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className='text-white/40'>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              )}
              <AuthorContent data={article.authors} />
            </div>
          </div>
          <div className='lg:pt-10 lg:max-w-[340px] text-white lg:pb-16 space-y-8 lg:space-y-10'>
            <RelatedReport connectedWith={article.connected_with} />
            <RelatedContentSidebar
              title='Related Videos & Podcasts'
              currentContentId={article.uuid}
              contentType={CONTENT_TYPES.VIDEOS_AND_PODCAST_EPISODES}
            />
            <RelatedContentSidebar
              title='Did you check these out?'
              currentContentId={article.uuid}
            />
          </div>
        </div>
        <Subscribe />
      </Layout>
    </div>
  );
}

export async function getServerSideProps({ req, query }) {
  try {
    const response = await getContentDetailBySlug(query.slug, {
      params: {
        contentTypeName: CONTENT_TYPES.ARTICLES,
      },
    });
    return {
      props: {
        article: response.data.data,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}

export default ArticleDetail;
